import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../context/UserContext";
import { profilesApi, usersApi } from "../../api";
import "../../css/components/AvatarUploader.css";
import HrnbButton from "../controls/HrnbButton";
import { DeleteIcon, UploadIcon } from "../../icons";
import HrnbImage from "../controls/HrnbImage";
import HrnbContainer, { HrnbRow } from "../controls/HrnbContainer";
import HrnbFileUpload from "../controls/HrnbFileUpload";
import { DefaultAvatar } from "../../images";

export default function AvatarUploader({ profileId }) {
    const { setCurrentUser } = useContext(UserContext);
    const [selectedFile, setSelectedFile] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let objectUrl = null;
        const fetchAvatar = async () => {
            if (!profileId) return;
            setLoading(true);
            setError("");
            try {
                const p = await profilesApi.get(profileId);
                const avatarId = p?.avatar;
                if (!avatarId) {
                    setAvatarUrl(null);
                    setLoading(false);
                    return;
                }
                const blob = await profilesApi.getAvatar(avatarId);
                objectUrl = URL.createObjectURL(blob);
                setAvatarUrl(objectUrl);
            } catch (err) {
                console.error('[AvatarUploader] Failed to load avatar:', err.message);
                setError("Не удалось загрузить аватар");
                setAvatarUrl(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAvatar();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [profileId]);

    const handleFileSelect = (e) => {
        const file = e?.target?.files?.[0] || null;
        setSelectedFile(file);
    };

    const handleUploadAvatar = async () => {
        if (!selectedFile || !profileId) return;
        setLoading(true);
        setError("");

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const updatedProfile = await profilesApi.uploadAvatar(profileId, formData);
            const avatarId = updatedProfile?.avatar;

            if (avatarId) {
                const blob = await profilesApi.getAvatar(avatarId);
                const objectUrl = URL.createObjectURL(blob);
                setAvatarUrl(objectUrl);
            }

            // Обновляем данные текущего пользователя
            try {
                const user = await usersApi.current();
                if (setCurrentUser) {
                    setCurrentUser(user);
                }
            } catch (e) {
                console.warn('[AvatarUploader] Failed to refresh user after upload:', e.message);
            }
            setSelectedFile(null);
        } catch (err) {
            console.error('[AvatarUploader] Failed to upload avatar:', err.message);
            setError("Ошибка при загрузке аватара");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAvatar = async () => {
        if (!profileId) return;
        setLoading(true);
        setError("");
        try {
            await profilesApi.deleteAvatar(profileId);
            setAvatarUrl(null);
            // Обновляем данные текущего пользователя
            try {
                const user = await usersApi.current();
                if (setCurrentUser) {
                    setCurrentUser(user);
                }
            } catch (e) {
                console.warn('[AvatarUploader] Failed to refresh user after delete:', e.message);
            }
        } catch (err) {
            console.error('[AvatarUploader] Failed to delete avatar:', err.message);
            setError("Ошибка при удалении аватара");
        } finally {
            setLoading(false);
        }
    };

    return (
        <HrnbContainer>
            <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                <HrnbImage src={avatarUrl || DefaultAvatar} size={350} rounded alt="Аватар пользователя" />
            </HrnbRow>

            <div className="avatar-controls">
                <HrnbFileUpload
                    label="Аватар"
                    accept="image/png, image/jpeg"
                    onChange={handleFileSelect}
                    multiple={false}
                    value={selectedFile}
                    disabled={loading}
                    hint="PNG или JPEG, до 2 МБ"
                />

                <HrnbButton
                    variant="primary"
                    onClick={handleUploadAvatar}
                    disabled={!selectedFile || loading}
                    icon={<UploadIcon />}
                >
                    {loading ? "Загрузка..." : "Загрузить аватар"}
                </HrnbButton>

                <HrnbButton
                    variant="primary"
                    onClick={handleDeleteAvatar}
                    disabled={!avatarUrl || loading}
                    icon={<DeleteIcon />}
                >
                    Удалить аватар
                </HrnbButton>
            </div>

            {error && <div className="error text-danger">{error}</div>}
        </HrnbContainer>
    );

}

